// Take a loan — adds cash to wallet, creates a LoanInstance with precomputed payment.

import type { GameState, LoanInstance } from '../state/types.js';
import type { TuningDef } from '../config/types.js';
import { applyTxn } from '../ledger/append.js';
import { weeklyLoanPayment } from './amortise.js';

export type TakeLoanError = {
  code: 'INVALID_PRINCIPAL' | 'EXCEEDS_LIMIT';
  message: string;
};

export type TakeLoanOutcome = { state: GameState; loanId: string };

export function takeLoan(
  state: GameState,
  args: { principal: number; loanId: string; termWeeks?: number },
  tuning: TuningDef,
): { ok: true; value: TakeLoanOutcome } | { ok: false; error: TakeLoanError } {
  if (args.principal <= 0) {
    return { ok: false, error: { code: 'INVALID_PRINCIPAL', message: 'Loan must be positive.' } };
  }
  // Soft cap — Phase 2 will tie this to creditScore. For MVP we just use the configured max.
  if (args.principal > tuning.loan.maxPrincipalAtStart) {
    return {
      ok: false,
      error: {
        code: 'EXCEEDS_LIMIT',
        message: `Loan limit is B$${tuning.loan.maxPrincipalAtStart}.`,
      },
    };
  }

  const termWeeks = args.termWeeks ?? tuning.loan.defaultTermWeeks;
  const ratePerWeek = tuning.loan.ratePerWeek;
  const weeklyPayment = weeklyLoanPayment(args.principal, ratePerWeek, termWeeks);

  const loan: LoanInstance = {
    id: args.loanId,
    principal: args.principal,
    ratePerWeek,
    termWeeks,
    weeklyPayment,
    paymentsMade: 0,
    remainingPrincipal: args.principal,
  };

  const next = applyTxn(state, {
    type: 'loan_in',
    amount: args.principal,
    category: 'capital',
    note: `Loan ${loan.id} taken (term ${termWeeks}w @ ${(ratePerWeek * 100).toFixed(1)}%/wk)`,
  });

  return {
    ok: true,
    value: {
      state: { ...next, loans: [...next.loans, loan] },
      loanId: args.loanId,
    },
  };
}
